import React, { useState, useEffect, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AuctionDataGrid = ({ auctions, selectedAuction, onAuctionSelect, filters, sortConfig, onSort }) => {
  const [hoveredRow, setHoveredRow] = useState(null);
  const [audioEnabled, setAudioEnabled] = useState(true);

  const filteredAndSortedAuctions = useMemo(() => {
    let filtered = auctions?.filter(auction => {
      if (filters?.status && filters?.status !== 'all' && auction?.status !== filters?.status) return false;
      if (filters?.bidRange?.min && auction?.currentBid < filters?.bidRange?.min) return false;
      if (filters?.bidRange?.max && auction?.currentBid > filters?.bidRange?.max) return false;
      if (filters?.state && auction?.callMetadata?.state !== filters?.state) return false;
      if (filters?.buyerCount && auction?.participatingBuyers?.length < filters?.buyerCount) return false;
      return true;
    });

    if (sortConfig?.key) {
      filtered?.sort((a, b) => {
        let aValue = sortConfig?.key?.includes('.') 
          ? sortConfig?.key?.split('.')?.reduce((obj, key) => obj?.[key], a)
          : a?.[sortConfig?.key];
        let bValue = sortConfig?.key?.includes('.') 
          ? sortConfig?.key?.split('.')?.reduce((obj, key) => obj?.[key], b)
          : b?.[sortConfig?.key];

        if (typeof aValue === 'string') {
          aValue = aValue?.toLowerCase();
          bValue = bValue?.toLowerCase();
        }

        if (aValue < bValue) return sortConfig?.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig?.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [auctions, filters, sortConfig]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-success';
      case 'closing': return 'text-warning';
      case 'completed': return 'text-muted-foreground';
      case 'cancelled': return 'text-error';
      default: return 'text-foreground';
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active': return 'bg-success/10 text-success';
      case 'closing': return 'bg-warning/10 text-warning';
      case 'completed': return 'bg-muted text-muted-foreground';
      case 'cancelled': return 'bg-error/10 text-error';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const formatTimeRemaining = (endTime) => {
    const now = new Date();
    const remaining = endTime - now;
    
    if (remaining <= 0) return '00:00';
    
    const minutes = Math.floor(remaining / 60000);
    const seconds = Math.floor((remaining % 60000) / 1000);
    return `${minutes?.toString()?.padStart(2, '0')}:${seconds?.toString()?.padStart(2, '0')}`;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    })?.format(amount);
  };

  const handleSort = (key) => {
    onSort(key);
  };

  const SortableHeader = ({ label, sortKey, className = "" }) => (
    <th 
      className={`px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground transition-smooth ${className}`}
      onClick={() => handleSort(sortKey)}
    >
      <div className="flex items-center space-x-1">
        <span>{label}</span>
        {sortConfig?.key === sortKey && (
          <Icon 
            name={sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
            size={14} 
          />
        )}
      </div>
    </th>
  );

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Grid Header */}
      <div className="bg-muted/30 px-4 py-3 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h3 className="text-sm font-semibold text-foreground">
              Active Auctions ({filteredAndSortedAuctions?.length})
            </h3>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-success rounded-full pulse-live"></div>
              <span className="text-xs text-muted-foreground">Live Updates</span>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setAudioEnabled(!audioEnabled)}
              iconName={audioEnabled ? "Volume2" : "VolumeX"}
              title={audioEnabled ? "Disable audio alerts" : "Enable audio alerts"}
            />
            <Button
              variant="outline"
              size="sm"
              iconName="Download"
            >
              Export
            </Button>
          </div>
        </div>
      </div>
      {/* Data Grid */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/20">
            <tr>
              <SortableHeader label="Auction ID" sortKey="id" />
              <SortableHeader label="Call Type" sortKey="callMetadata.type" />
              <SortableHeader label="State" sortKey="callMetadata.state" />
              <SortableHeader label="Age" sortKey="callMetadata.age" />
              <SortableHeader label="Current Bid" sortKey="currentBid" />
              <SortableHeader label="Buyers" sortKey="participatingBuyers.length" />
              <SortableHeader label="Time Left" sortKey="endTime" />
              <SortableHeader label="Status" sortKey="status" />
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filteredAndSortedAuctions?.map((auction) => (
              <tr
                key={auction?.id}
                className={`hover:bg-muted/30 transition-smooth cursor-pointer ${
                  selectedAuction?.id === auction?.id ? 'bg-accent/10 border-l-4 border-l-accent' : ''
                } ${hoveredRow === auction?.id ? 'bg-muted/20' : ''}`}
                onClick={() => onAuctionSelect(auction)}
                onMouseEnter={() => setHoveredRow(auction?.id)}
                onMouseLeave={() => setHoveredRow(null)}
              >
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-mono text-foreground">
                      {auction?.id}
                    </span>
                    {auction?.priority === 'high' && (
                      <Icon name="AlertTriangle" size={14} className="text-warning" />
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="flex items-center space-x-2">
                    <Icon name="Phone" size={14} className="text-muted-foreground" />
                    <span className="text-sm text-foreground">
                      {auction?.callMetadata?.type}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <span className="text-sm text-foreground">
                    {auction?.callMetadata?.state}
                  </span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <span className="text-sm text-foreground">
                    {auction?.callMetadata?.age}
                  </span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-semibold text-foreground">
                      {formatCurrency(auction?.currentBid)}
                    </span>
                    {auction?.bidIncrement > 0 && (
                      <div className="flex items-center space-x-1 text-success">
                        <Icon name="TrendingUp" size={12} />
                        <span className="text-xs">+{formatCurrency(auction?.bidIncrement)}</span>
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-foreground">
                      {auction?.participatingBuyers?.length}
                    </span>
                    <div className="flex -space-x-1">
                      {auction?.participatingBuyers?.slice(0, 3)?.map((buyer, index) => (
                        <div
                          key={buyer?.id}
                          className="w-6 h-6 bg-primary rounded-full border-2 border-card flex items-center justify-center"
                          title={buyer?.name}
                        >
                          <span className="text-xs text-primary-foreground font-medium">
                            {buyer?.name?.charAt(0)}
                          </span>
                        </div>
                      ))}
                      {auction?.participatingBuyers?.length > 3 && (
                        <div className="w-6 h-6 bg-muted rounded-full border-2 border-card flex items-center justify-center">
                          <span className="text-xs text-muted-foreground">
                            +{auction?.participatingBuyers?.length - 3}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="flex items-center space-x-2">
                    <Icon name="Clock" size={14} className="text-muted-foreground" />
                    <span className={`text-sm font-mono ${
                      new Date(auction.endTime) - new Date() < 60000 ? 'text-error' : 'text-foreground'
                    }`}>
                      {formatTimeRemaining(new Date(auction.endTime))}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(auction?.status)}`}>
                    {auction?.status}
                  </span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="flex items-center space-x-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Pause"
                      title="Pause auction"
                      onClick={(e) => {
                        e?.stopPropagation();
                        // Handle pause action
                      }}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="MoreHorizontal"
                      title="More actions"
                      onClick={(e) => {
                        e?.stopPropagation();
                        // Handle more actions
                      }}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {filteredAndSortedAuctions?.length === 0 && (
        <div className="p-8 text-center">
          <Icon name="Search" size={32} className="text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">No auctions match your current filters</p>
        </div>
      )}
    </div>
  );
};

export default AuctionDataGrid;